import { StepConfig } from "@verity/components/core";
import { useReducer } from "react";

type stateType = {
	currentStepIdx: number;
	currentStepKey: string;
	completedSteps: number[];
};

const initialState: stateType = {
	currentStepIdx: 0,
	currentStepKey: "details",
	completedSteps: [],
};

type stepperActionType = "NEXT_STEP" | "PREV_STEP";
type stepperAction = { type: stepperActionType };

type goToStepAction = { type: "GO_TO_STEP"; payload: number };

type actionType = stepperAction | goToStepAction;

export default function useStepCounter(steps: StepConfig<string>[], isEdit: boolean) {
	function getIdxFromKey(key: string) {
		const step = steps.find((s) => s.key === key);
		return step ? steps.indexOf(step) : 0;
	}

	function hasSubstep(key: string) {
		return steps.some((s) => s.parentSubstep === key);
	}

	function isLastSubstep(idx: number) {
		const parentKey = steps[idx].parentSubstep;
		return !steps.slice(idx + 1).some((s) => s.parentSubstep === parentKey);
	}

	function handleCurrentStep(idx: number, flowForwards: boolean = true) {
		const key = steps[idx].key;

		//Do not try and sit at parent step
		if (hasSubstep(key)) {
			const newIdx = flowForwards ? idx + 1 : idx - 1;
			return {
				currentStepIdx: newIdx,
				currentStepKey: steps[newIdx].key,
			};
		} else
			return {
				currentStepIdx: idx,
				currentStepKey: key,
			};
	}

	function handleCompletedStep(oldIdx: number) {
		//Log parent as complete if done its children
		if (isLastSubstep(oldIdx)) return [oldIdx, getIdxFromKey(steps[oldIdx].parentSubstep!)];
		else return [oldIdx];
	}

	function reducer(state: stateType, action: actionType) {
		const idx = state.currentStepIdx;
		switch (action.type) {
			case "NEXT_STEP": {
				if (idx < steps.length - 1) {
					return {
						...state,
						...handleCurrentStep(idx + 1),
						completedSteps: [...state.completedSteps, ...handleCompletedStep(idx)],
					};
				}
				return state;
			}
			case "PREV_STEP": {
				if (idx === 0) return state;
				return {
					...state,
					...handleCurrentStep(idx - 1, false),
				};
			}
			case "GO_TO_STEP": {
				return {
					...state,
					...handleCurrentStep(action.payload),
				};
			}
		}
	}

	const [state, dispatch] = useReducer(
		reducer,
		isEdit
			? {
					currentStepIdx: 0,
					currentStepKey: "details",
					completedSteps: steps.map((_, idx) => idx),
				}
			: initialState,
	);

	const onNext = () => dispatch({ type: "NEXT_STEP" });
	const onBack = () => dispatch({ type: "PREV_STEP" });
	const goToStep = (idx: number) => dispatch({ type: "GO_TO_STEP", payload: idx });

	return { state, onNext, onBack, goToStep };
}
